import { EmailContentView } from "components/EmailContentView";
import { RelativeTimeDisplay } from "components/RelativeTimeDisplay/RelativeTimeDisplay";
import { Button } from "components/ui/Button";
import { X } from "lucide-react";
import type { FC } from "react";
import type { EmailWithStatus } from "routes/emails/SearchEmailsPage";

/** プレビューから全画面詳細へ遷移する際の初期パネル指定 */
export type PreviewOpenPanel = "reply" | undefined;

export type EmailPreviewPaneProps = {
  email: EmailWithStatus;
  freeWords: string[];
  onClose: () => void;
  onOpenDetail: (panel?: PreviewOpenPanel) => void;
};

/**
 * 検索結果一覧の右に表示する軽量プレビューPane。
 * 本文の閲覧のみを担い、返信やマッチングなど下書き・課金を伴う重い操作は
 * 「詳細を開く / 返信する」ボタンで全画面詳細 (/emails/search/:id) に卒業させる。
 * @param {EmailPreviewPaneProps} props email / freeWords / onClose / onOpenDetail
 * @return {JSX.Element} プレビューPane
 */
export const EmailPreviewPane: FC<EmailPreviewPaneProps> = ({
  email,
  freeWords,
  onClose,
  onOpenDetail,
}) => {
  return (
    <div className="flex flex-col h-full rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* ヘッダ */}
      <div className="flex items-start gap-2 bg-primary text-white px-4 py-3 shrink-0">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold line-clamp-2">{email.subject}</h3>
          <p className="text-xs text-white/70 mt-1 truncate">
            {`from: ${email.from.name}（${email.from.address}）`}
          </p>
          <p className="text-xs text-white/70">
            date: <RelativeTimeDisplay date={email.date} />
          </p>
        </div>
        <button
          type="button"
          aria-label="プレビューを閉じる"
          title="プレビューを閉じる"
          onClick={onClose}
          className="shrink-0 rounded p-1 text-white/90 hover:bg-white/20 active:bg-white/30"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* 本文 */}
      <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
        <EmailContentView
          content={email.content}
          freeWords={freeWords}
          highlighterType="Modal"
        />
      </div>

      {/* フッタ (卒業ボタン) */}
      <div className="shrink-0 border-t border-slate-200 p-3">
        <p className="text-xs text-slate-400 mb-2">
          返信・マッチングは全画面で（下書きは保護されます）
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            width="full"
            onClick={() => onOpenDetail()}
          >
            詳細を開く
          </Button>
          <Button
            type="button"
            size="sm"
            width="full"
            onClick={() => onOpenDetail("reply")}
          >
            返信する
          </Button>
        </div>
      </div>
    </div>
  );
};
